import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfettiThrower } from '../../hooks/useConfettiThrower';
import { trackEvent } from '../../utils/analytics';
import { shuffleWithSeed } from '../../utils/shuffleWithSeed';
import { MatchItem, MatchModeConfig } from './matchConfig';

/** Four labels and their four images per round. */
export const BATCH_SIZE = 4;

/** Rounds in one session — 5 x 4 = 20 items. */
export const SESSION_ROUNDS = 5;

/** A perfect round celebrates for this long, then moves on by itself. */
export const ADVANCE_DELAY_MS = 1000;

/**
 * How long the player's own wrong placements stay on screen before the flags
 * move to the right answer. Long enough to see what you did, short enough not
 * to feel like a penalty.
 */
export const WRONG_REVEAL_MS = 900;

export type RoundPhase = 'placing' | 'revealing' | 'resolved';

export type RoundState = {
  /** Fixed rows, top to bottom. */
  cities: MatchItem[];
  /** Stable display order for the tray. */
  trayOrder: number[];
  /** placements[slot] = index into `cities`, or null while the slot is empty. */
  placements: (number | null)[];
  phase: RoundPhase;
  /** Per slot, set when the player checks. Null while still placing. */
  results: boolean[] | null;
  /** Zero-based round within the session. */
  index: number;
};

export type Tally = { correct: number; total: number };

/**
 * Takes the next `BATCH_SIZE` cards from the shuffled pool, wrapping at the
 * end. Skips a card whose name is already in the round: two cities are called
 * Santo Domingo, and a round containing both would have two defensible answers
 * for the same row.
 */
const takeRound = (pool: MatchItem[], start: number) => {
  const cities: MatchItem[] = [];
  let cursor = start;
  let scanned = 0;

  while (cities.length < BATCH_SIZE && scanned < pool.length) {
    const card = pool[cursor % pool.length];
    cursor += 1;
    scanned += 1;
    if (cities.some((city) => city.name === card.name)) continue;
    cities.push(card);
  }

  return { cities, cursor: cursor % pool.length };
};

/** Warms the browser cache so the next round paints instantly. */
const preload = (cards: MatchItem[]) => {
  for (const card of cards) {
    const image = new Image();
    image.src = card.flag;
  }
};

const newRound = (
  cities: MatchItem[],
  index: number,
  seed: string,
): RoundState => ({
  cities,
  trayOrder: shuffleWithSeed(
    cities.map((_, flag) => flag),
    `${seed}-tray-${index}`,
  ),
  placements: cities.map(() => null),
  phase: 'placing',
  results: null,
  index,
});

export const useMatchSession = (config: MatchModeConfig) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const throwConfetti = useConfettiThrower();

  const seedRef = useRef(`${config.key}-${Date.now()}-${Math.random()}`);
  const poolRef = useRef<MatchItem[] | null>(null);
  const cursorRef = useRef(0);
  const pendingRef = useRef<MatchItem[] | null>(null);

  const [round, setRound] = useState<RoundState | null>(null);
  const [tally, setTally] = useState<Tally>({ correct: 0, total: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const pool = shuffleWithSeed(
        await config.loadItems(language),
        seedRef.current,
      );

      if (cancelled) return;
      if (pool.length < BATCH_SIZE) {
        setLoadFailed(true);
        return;
      }

      const first = takeRound(pool, 0);
      const second = takeRound(pool, first.cursor);

      poolRef.current = pool;
      cursorRef.current = second.cursor;
      pendingRef.current = second.cities;
      preload(second.cities);

      setRound(newRound(first.cities, 0, seedRef.current));
      trackEvent('mode_start', { mode: config.key, pool_size: pool.length });
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [config, language]);

  /** Moves a flag into a slot, from the tray or from another slot. */
  const placeFlag = useCallback((flag: number, slot: number) => {
    setRound((previous) => {
      if (!previous || previous.phase !== 'placing') return previous;

      const placements = previous.placements.map((placed) =>
        placed === flag ? null : placed,
      );
      placements[slot] = flag;
      return { ...previous, placements };
    });
  }, []);

  /** Sends whatever is in a slot back to the tray. */
  const clearSlot = useCallback((slot: number) => {
    setRound((previous) => {
      if (!previous || previous.phase !== 'placing') return previous;
      if (previous.placements[slot] === null) return previous;

      const placements = [...previous.placements];
      placements[slot] = null;
      return { ...previous, placements };
    });
  }, []);

  const isComplete = round
    ? round.placements.every((placed) => placed !== null)
    : false;

  const check = useCallback(() => {
    if (!round || round.phase !== 'placing') return;
    if (round.placements.some((placed) => placed === null)) return;

    const results = round.placements.map((flag, slot) => flag === slot);
    const correct = results.filter(Boolean).length;
    const allCorrect = correct === round.cities.length;

    setTally((previous) => ({
      correct: previous.correct + correct,
      total: previous.total + round.cities.length,
    }));
    setRound({
      ...round,
      results,
      phase: allCorrect ? 'resolved' : 'revealing',
    });

    if (allCorrect) throwConfetti();

    trackEvent('mode_batch_result', {
      mode: config.key,
      round: round.index + 1,
      correct_count: correct,
      batch_size: round.cities.length,
      accuracy: Math.round((correct / round.cities.length) * 100),
    });
  }, [config.key, round, throwConfetti]);

  const advance = useCallback(() => {
    const pool = poolRef.current;
    if (!pool || !round) return;

    if (round.index + 1 >= SESSION_ROUNDS) {
      setIsFinished(true);
      return;
    }

    const cards =
      pendingRef.current ?? takeRound(pool, cursorRef.current).cities;
    const following = takeRound(pool, cursorRef.current);

    cursorRef.current = following.cursor;
    pendingRef.current = following.cities;
    preload(following.cities);

    setRound(newRound(cards, round.index + 1, seedRef.current));
  }, [round]);

  /** New session, continuing through the pool rather than repeating cities. */
  const restart = useCallback(() => {
    const pool = poolRef.current;
    if (!pool) return;

    const cards =
      pendingRef.current ?? takeRound(pool, cursorRef.current).cities;
    const following = takeRound(pool, cursorRef.current);

    cursorRef.current = following.cursor;
    pendingRef.current = following.cities;
    preload(following.cities);

    setTally({ correct: 0, total: 0 });
    setIsFinished(false);
    setRound(newRound(cards, 0, `${seedRef.current}-${Date.now()}`));
    trackEvent('mode_start', { mode: config.key, pool_size: pool.length });
  }, [config.key]);

  // The player's wrong placements linger, then the flags move to the answer so
  // every round ends correct on screen.
  useEffect(() => {
    if (round?.phase !== 'revealing') return;

    const timer = setTimeout(() => {
      setRound((previous) =>
        previous && previous.phase === 'revealing'
          ? {
              ...previous,
              placements: previous.cities.map((_, slot) => slot),
              phase: 'resolved',
            }
          : previous,
      );
    }, WRONG_REVEAL_MS);

    return () => clearTimeout(timer);
  }, [round?.phase]);

  // Only a clean sweep advances on its own; after a mistake the corrections
  // stay up until the player is ready.
  useEffect(() => {
    if (round?.phase !== 'resolved') return;
    if (round.results?.some((correct) => !correct)) return;

    const timer = setTimeout(advance, ADVANCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [round?.phase, round?.results, advance]);

  // Reported once the final round has been acknowledged.
  useEffect(() => {
    if (!isFinished) return;
    trackEvent('mode_session_end', {
      mode: config.key,
      score: tally.correct,
      total: tally.total,
      accuracy: tally.total
        ? Math.round((tally.correct / tally.total) * 100)
        : 0,
      rounds: SESSION_ROUNDS,
      completed: true,
    });
  }, [config.key, isFinished, tally.correct, tally.total]);

  /*
    A player who walks away mid-session — Back, the menu, or closing the tab —
    would otherwise leave no trace at all, so completed and abandoned sessions
    could not be told apart in reporting. The ref carries the latest values into
    the unmount cleanup, which would otherwise close over the first render.
  */
  const latest = useRef({ tally, isFinished, round });
  latest.current = { tally, isFinished, round };

  useEffect(() => {
    const report = () => {
      const { tally: t, isFinished: done, round: current } = latest.current;
      if (done || t.total === 0) return;
      trackEvent('mode_session_abandoned', {
        mode: config.key,
        score: t.correct,
        total: t.total,
        accuracy: Math.round((t.correct / t.total) * 100),
        rounds_played: (current?.index ?? 0) + 1,
        rounds: SESSION_ROUNDS,
        completed: false,
      });
    };

    // pagehide covers closing the tab or backgrounding on iOS, where unmount
    // never runs; the flag stops it being reported twice.
    let reported = false;
    const once = () => {
      if (reported) return;
      reported = true;
      report();
    };

    window.addEventListener('pagehide', once);
    return () => {
      window.removeEventListener('pagehide', once);
      once();
    };
  }, [config.key]);

  return {
    round,
    tally,
    isFinished,
    isComplete,
    loadFailed,
    isLoading: !round && !loadFailed,
    placeFlag,
    clearSlot,
    check,
    advance,
    restart,
  };
};
